<script
  setup
  lang="ts"
>

import { computed, ref } from 'vue';
import mtTimeIntervalField from '@/components/composite/mtTimeIntervalField.vue';
import mtButton from '@/components/UI/mtButton/index.vue';
import Alert from '@/components/UI/AppAlert/AppAlert.vue';
import AppSelect from '@/components/UI/AppSelect/AppSelect.vue';
import AppInput from '@/components/UI/AppInput/AppInput.vue';
import AppCheckbox from '@/components/UI/AppCheckbox/AppCheckbox.vue';
import AppTextarea from '@/components/UI/AppTextarea/AppTextarea.vue';
import useVuelidate from '@vuelidate/core';
import useToast from '@/components/UI/AppToast/useToast.js';
import AppFilesUploader from '@/components/composite/AppFilesUploader.vue';
import { deepClone } from '@/services/utils.js';
import MtLoadingAnimationLinear from '@/components/UI/mtLoadingAnimationLinear/mtLoadingAnimationLinear.vue';
import TaskType from '@/enums/task-type.enum.ts';
import DoubleCheckIcon from '@/components/icons/DoubleCheckIcon.vue';
import TestsAdminApi from '@/api/tests-admin.api.ts';
import { useValidations } from '@/plugins/vuelidate-wrapper';
import AppAutocomplete from '@/components/UI/AppAutocomplete/AppAutocomplete.vue';
import AppTooltip from '@/components/UI/AppTooltip/AppTooltip.vue';
import mtSwitch from '@/components/UI/mtSwitch/index.vue';
import { useStore } from 'vuex';
import assessmentApi, { AssessmentMetric } from '@/api/assessment.api';

interface Props {
  submit: (payload: any) => Promise<any>;
  predefinedData?: any;
  testsForExclude: any[];
  buttonText?: string;
  resetAfterSubmit?: boolean;
  type?: TaskType;
  isPro?: boolean;
  isEdit?: boolean;
  isUpdate: boolean;
}

const defaultValue = {
  mainTest: null,
  name: null,

  description: null,
  description_expert: null,
  group: null,
  date_from: null,
  date_to: null,
  number_attempt: 1,
  resultAttempt: '',
  input_text: true,
  upload_files: true,
  enable_proctoring: false,
  files: [],
  expert_files: [],
  audience: [],
  view_respondent_expert_task: true,
  assessment_metric: null,
  create_expert_task: false,
};

const { requiredIf, required } = useValidations();

const store = useStore();

const props = withDefaults(defineProps<Props>(), {
  buttonText: 'Применить',
  resetAfterSubmit: true,
  type: TaskType.TEST,
  isPro: false,
  isEdit: false
});

const computedParamType = computed(() => {
  if (props.type === TaskType.TEST || props.type === TaskType.TEST_PRO) return 'auto';

  return 'expert';
});

const proctoringProviders = computed(() => {
  let result = [];

  if (proctoringSettings.value.proctorEdu?.enabled) {
    result.push({
      name: 'ProctorEdu',
      slug: 'proctorEdu'
    });
  }

  if (proctoringSettings.value.examus?.enabled) {
    result.push({
      name: 'Examus',
      slug: 'examus'
    });
  }
  return result;
});

const proctoringSettings = computed(() => {
  return store.getters['authUser']?.proctoring || {};
});

const proctoringEnabled = computed(() => {
  return proctoringSettings.value.proctorEdu?.enabled || proctoringSettings.value.examus?.enabled || false;
});

const initialValue = props.predefinedData || defaultValue;
const payload = ref({
  ...initialValue,
  mainTest: props.predefinedData?.mainTest?.uuid || null,
  proctoring_config: {
    provider: props.predefinedData?.proctoring_config?.provider || proctoringProviders.value[0]?.slug || 'proctorEdu',
    examus_type: props.predefinedData?.proctoring_config?.examus_type || 'auto',
    examus_session_duration: props.predefinedData?.proctoring_config?.examus_session_duration || 60,
    examus_identification_type: props.predefinedData?.proctoring_config?.examus_identification_type || 'face',
    examus_allowed_rules: props.predefinedData?.proctoring_config?.examus_allowed_rules || []
  }
});

const attempts = [
  {
    value: 1,
    name: '1'
  },
  {
    value: 2,
    name: '2'
  },
  {
    value: 3,
    name: '3'
  },
  {
    value: 4,
    name: '4'
  },
  {
    value: 5,
    name: '5'
  },
  {
    value: -1,
    name: '∞'
  }
];
const resultAttempts = [
  {
    slug: 'first',
    name: 'Первую'
  },
  {
    slug: 'last',
    name: 'Последнюю'
  },
  {
    slug: 'best',
    name: 'Лучшую'
  }
];

const examusAllowedRules = [
  {
    slug: 'allow_to_use_websites',
    name: 'Использование вебсайтов'
  },
  {
    slug: 'allow_to_use_books',
    name: 'Использование книг'
  },
  {
    slug: 'allow_to_use_paper',
    name: 'Использование черновиков'
  },
  {
    slug: 'allow_to_use_messengers',
    name: 'Использование мессенджеров'
  },
  {
    slug: 'allow_to_use_calculator',
    name: 'Использование калькулятора'
  },
  {
    slug: 'allow_to_use_excel',
    name: 'Использование Excel'
  },
  {
    slug: 'allow_to_use_human_assistant',
    name: 'Помощь людей'
  },
  {
    slug: 'allow_absence_in_frame',
    name: 'Выход из комнаты'
  },
  {
    slug: 'allow_voices',
    name: 'Голоса'
  },
  {
    slug: 'allow_wrong_gaze_direction',
    name: 'Увод взгляда'
  }
];

const mainTests = ref([]);
const autocompleteRef = ref();
const computedMainTests = computed(() => {
  const uniqueMainTests = mainTests.value.map((item: any) => {
    item.alreadyExists = props.testsForExclude.includes(item.uuid);
    item.readonly = item.alreadyExists;
    item.disabled = !item.status;

    return item;
  });

  uniqueMainTests.sort();
  return uniqueMainTests;
});
const computedMainTestsOptions = computed(() => {
  let uniqueMainTestsOptions = computedMainTests.value;
  if (payload.value.group) {
    uniqueMainTestsOptions = uniqueMainTestsOptions.filter(item => item.group === payload.value.group);
  }

  return uniqueMainTestsOptions;
});
const computedTestGroups = computed(() => {
  return [...new Set(
    mainTests.value
      .map((item: any) => item.group)
      .filter(Boolean)
  )].map(group => ({
    name: group,
    value: group
  })).sort((a, b) => a.value.localeCompare(b.value));
});

const computedCategories = computed(() => {
  const mainTest: any = mainTests.value.find((item: any) => item.uuid === payload.value.mainTest) ?? null;
  return mainTest ? mainTest.Categories : null;
});
const testsAreLoaded = ref(false);

let assessmentMetrics = ref([] as AssessmentMetric[])
assessmentApi
  .getMetrics()
  .then((metrics) => {
    assessmentMetrics.value = metrics;
  })

watch(() => props.isPro, fetchMainTests, { immediate: true });

async function fetchMainTests(isPro: any) {
  TestsAdminApi.getAll({
    page: 1,
    per_page: 10000,
    is_pro: isPro ? 1 : 0,
    settings: {}
  })
    .then((response) => {
      mainTests.value = response.data.data;
    })
    .finally(() => {
      testsAreLoaded.value = true;
    });
}

const noAvailableTests = computed(() => {
  if (props.isEdit) return false;
  return (props.type === TaskType.TEST || props.type === TaskType.TEST_PRO) &&
    computedMainTests.value.every(item => item.readonly) &&
    testsAreLoaded.value;
});

const isFileUploading = computed(() => {
  let result = false;

  if (payload.value.files.length) {
    payload.value.files.some((file: any) => {
      if (file.isLoading) {
        result = true;
        return true;
      }
    });
  }
  if (payload.value.expert_files.length) {
    payload.value.expert_files.some((file: any) => {
      if (file.isLoading) {
        result = true;
        return true;
      }
    });
  }

  return result;
});

function setDates(dates: any) {
  payload.value.date_from = dates.from;
  payload.value.date_to = dates.to;
}

function setGroup() {
  const mainTest: any = mainTests.value.find(
    (item: any) => item.uuid === payload.value.mainTest
  );
  if (mainTest && mainTest.group!==payload.value.group) {
    payload.value.mainTest=null;
    payload.value.name=null;
  }
}

function setTaskName() {
  if (payload.value.type === 'expert') {
    payload.value.name = '';
    return;
  }

  const mainTest: any = mainTests.value.find(
    (item: any) => item.uuid === payload.value.mainTest
  );
  if (mainTest) {
    payload.value.name = mainTest.name;
  }
}

const requestPending = ref(false);

const v$ = useVuelidate(
  {
    mainTest: {
      required: requiredIf(() => {
        return payload.value.type === 'test' || payload.value.type === 'test_pro';
      })
    },
    name: {
      required
    },
    number_attempt: {
      required: requiredIf(() => payload.value.type === 'test' || payload.value.type === 'test_pro')
    },
    registration_attempts: {
      required: requiredIf(() => payload.value.number_attempt > 1)
    }
  },
  payload
);

const { toast } = useToast();

async function submit() {
  const isValid = await v$.value.$validate();

  if (!isValid) {
    toast.error('Пожалуйста, введите корректные данные');
    return;
  }

  requestPending.value = true;
  props.submit({
    ...payload.value,
    type: props.type,
    parameters: {
      param_type: computedParamType.value
    }
  })
    .then(() => {
      if (props.resetAfterSubmit) {
        payload.value = deepClone(initialValue);
        payload.value.files = [];
        payload.value.expert_files = [];
        autocompleteRef.value?.clearSearch();
        v$.value.$reset();
      }
    })
    .finally(() => {
      requestPending.value = false;
    });
}
</script>

<template>
  <div class="tasks-form">
    <div class="tasks-form__scrollable">
      <div class="tasks-form__header">
        <slot name="title" />
      </div>
      <div class="tasks-form__body">
        <div class="tasks-form__content">
          <div
            v-if="noAvailableTests"
            class="mb-2"
          >
            <alert type="error">
              Нет доступных тестов
            </alert>
          </div>

          <div v-if="(props.type === TaskType.TEST_PRO) && computedTestGroups.length>0">
            <mt-loading-animation-linear v-if="!testsAreLoaded" />

            <app-autocomplete
              v-model="payload.group"
              :disabled="!!predefinedData"
              :items="computedTestGroups"
              item-value="value"
              item-text="name"
              label="Выбор группы"
              ref="autocompleteRef"
              class="tasks-form__test-select"
              :item-readonly="(item: any) => item.readonly"
              :item-disabled="(item: any) => item.disabled"
              :clearable="true"
              @change="setGroup"
            >
              <template #item="{item}">
                {{ item.value }}
              </template>
            </app-autocomplete>
          </div>

          <div v-if="props.type === TaskType.TEST || props.type === TaskType.TEST_PRO">
            <mt-loading-animation-linear v-if="!testsAreLoaded" />

            <app-autocomplete
              v-model="payload.mainTest"
              :items="computedMainTestsOptions"
              item-value="uuid"
              item-text="name"
              label="Выбор теста"
              ref="autocompleteRef"
              :disabled="!!predefinedData"
              @change="setTaskName"
              :errors="v$.mainTest.$errors"
              :item-readonly="(item: any) => item.readonly"
              :item-disabled="(item: any) => item.disabled"
              class="tasks-form__test-select"
              :clearClick="true"
            >
              <template #item="{item}">
                <double-check-icon
                  v-if="item.alreadyExists"
                  class="tasks-form__test-select__icon"
                  title="Тест уже добавлен"
                />
                {{ item.name }}
                <i v-if="!item.status"> - Отключен</i>
              </template>
            </app-autocomplete>
          </div>


          <template v-if="props.type === 'assessment'">
            <app-input
              v-if="isUpdate"
              :value="
                assessmentMetrics.find(
                  (x) => x.ModelName == payload.assessment_metric
                )?.ModelTitle
              "
              label="Базовая модель"
              disabled
            />
            <app-select
              v-else
              v-model="payload.assessment_metric"
              :items="assessmentMetrics"
              item-value="ModelName"
              item-text="ModelTitle"
              label="Базовая модель"
            />
          </template>
          <div v-if="props.type === TaskType.TEST || props.type === TaskType.TEST_PRO">
            <mt-loading-animation-linear v-if="!testsAreLoaded" />

            <app-autocomplete
              v-if="!!computedCategories"
              v-model="payload.CategoryId"
              :items="computedCategories"
              item-value="CategoryId"
              item-text="CategoryName"
              label="Выбор целевого уровня"
              ref="autocompleteRef"
              class="tasks-form__test-select"
              :clearClick="true"
            >
              <template #item="{item}">
                {{ item.CategoryName }}
              </template>
            </app-autocomplete>
          </div>

          <app-input
            v-model="payload.name"
            label="Название"
            :errors="v$.name?.$errors"
          />

          <mt-time-interval-field
            :date-from="payload.date_from"
            :date-to="payload.date_to"
            clearable
            withTime
            @update-date="setDates"
          />

          <template v-if="props.type === TaskType.TEST || props.type === TaskType.TEST_PRO">
            <app-files-uploader
              label="Прикрепленные файлы"
              v-model="payload.files"
              :max-size-mb="25"
              :max-total-size-mb="100"
            />
            <div class="tasks-form__row">
              <div class="field field--half">
                <app-select
                  v-model="payload.number_attempt"
                  :items="attempts"
                  :errors="v$.number_attempt?.$errors"
                  label="Количество попыток"
                  from-top
                  item-text="name"
                  item-value="value"
                />
              </div>
              <div
                :class="[
                  'field',
                  'field--half',
                  {
                    'field--disabled':
                      !payload.number_attempt || payload.number_attempt <= 1,
                  },
                ]"
              >
                <app-select
                  v-model="payload.registration_attempts"
                  :items="resultAttempts"
                  item-text="name"
                  item-value="slug"
                  label="Учитывать попытку"
                  from-top
                  :errors="v$.registration_attempts?.$errors"
                />
              </div>
            </div>
            <app-textarea v-model="payload.description">
              <template #label> Описание</template>
            </app-textarea>
          </template>
          <template v-if="props.type === TaskType.EXPERT">
            <div class="app-form-block">
              <div class="tasks-form__row">
                <div class="field field--half">
                  <app-checkbox v-model="payload.input_text">
                    <template #label>Ввод текста</template>
                  </app-checkbox>
                </div>
                <div class="field field--half">
                  <app-checkbox v-model="payload.upload_files">
                    <template #label>Загрузка файлов</template>
                  </app-checkbox>
                </div>
              </div>
              <app-checkbox v-model="payload.view_respondent_expert_task">
                <template #label>
                  Показывать респонденту экспертное задание
                </template>
              </app-checkbox>
              <div class="tasks-form__row">
                <div class="field field--half">
                  <app-files-uploader
                    label="Прикрепленные файлы для респондента"
                    v-model="payload.files"
                    :max-size-mb="25"
                    :max-total-size-mb="25"
                  />
                </div>
                <div class="field field--half">
                  <app-files-uploader
                    label="Прикрепленные файлы для эксперта"
                    v-model="payload.expert_files"
                    :max-size-mb="25"
                    :max-total-size-mb="25"
                  />
                </div>
              </div>
              <div class="tasks-form__row">
                <div class="field field--half">
                  <app-textarea
                    v-model="payload.description"
                    hide-details
                  >
                    <template #label> Инструкция для респондента</template>
                  </app-textarea>
                </div>
                <div class="field field--half">
                  <app-textarea
                    v-model="payload.description_expert"
                    hide-details
                  >
                    <template #label>Инструкция для эксперта</template>
                  </app-textarea>
                </div>
              </div>
            </div>
          </template>
          <template v-if="proctoringEnabled">
            <div class="app-form-block">
              <div class="tasks-form__row">
                <div class="field field--half">
                  <mt-switch
                    hide-details
                    label="Включить прокторинг"
                    v-model="payload.enable_proctoring"
                  />
                </div>
                <div class="field field--half" v-if="payload.enable_proctoring">
                  <app-select
                    :items='proctoringProviders'
                    item-value="slug"
                    item-text="name"
                    v-model="payload.proctoring_config.provider"
                    label="Провайдер"
                  />
                  <template v-if="payload.proctoring_config.provider==='proctorEdu'">
                    <app-input
                      v-model="payload.proctoring_config.proctor_edu_template"
                      label="Шаблон настроек сессии"
                      :errors="v$.name?.$errors"
                    />
                  </template>
                  <template v-else-if="payload.proctoring_config.provider==='examus'">
                    <app-input
                      v-model="payload.proctoring_config.examus_session_duration"
                      label="Длительность сессии прокторинга"
                      :errors="v$.name?.$errors"
                    />
                    <app-select
                      :items='[
                      {
                        name: "Синхронный",
                        slug: "online"
                      },
                      {
                        name: "Асинхронный",
                        slug: "offline"
                      },
                      {
                        name: "Автоматический (проверка алгоритмом)",
                        slug: "auto"
                      }
                    ]'
                      item-value="slug"
                      item-text="name"
                      v-model="payload.proctoring_config.examus_type"
                      label="Тип прокторинга"
                    />
                    <app-select
                      :items='[
                      {
                        name: "Документы",
                        slug: "passport"
                      },
                      {
                        name: "Лицо",
                        slug: "face"
                      },
                      {
                        name: "Лицо и документы",
                        slug: "face_and_passport"
                      },
                      {
                        name: "Пропустить",
                        slug: "skip"
                      }
                    ]'
                      item-value="slug"
                      item-text="name"
                      v-model="payload.proctoring_config.examus_identification_type"
                      label="Режим идентификации"
                    />
                    <div class="app-form-block app-form-block__allowed_rules">
                      <p class="app-form-block__label">Список разрешений:</p>
                      <app-checkbox
                        v-for="examusAllowedRule in examusAllowedRules"
                        v-model="payload.proctoring_config.examus_allowed_rules"
                        :value="examusAllowedRule.slug"
                      >
                        <template #label>{{ examusAllowedRule.name }}</template>
                      </app-checkbox>
                    </div>
                  </template>


                </div>
                <div class="field field--half" v-else></div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <div class="tasks-form__footer">
      <app-tooltip
        :disabled="!isFileUploading"
        class="tasks-form__button"
      >
        <template #activator>
          <mt-button
            class="tasks-form__button"
            :disabled="isFileUploading"
            :loading="requestPending"
            @click="submit()"
          >
            {{ props.buttonText }}
          </mt-button>
        </template>

        Пожалуйста, дождитесь окончания загрузки файлов
      </app-tooltip>
    </div>
  </div>
</template>

<style lang="scss">

.app-form-block__allowed_rules {

  .app-form-block__label {
    position: static;
    margin-bottom: 20px;
  }

  .app-form-block {
    margin-bottom: 20px;
  }
}

.tasks-form {
  height: 100%;

  &__test-select {
    &__icon {
      width: 16px;
      height: 16px;
      margin-right: 8px;
    }
  }

  .field--half {
    .mt-file-view {
      width: calc(100% - 8px);
    }
  }

  &__button {
    margin: 0 auto;
    width: 100%;
  }

  &__header {
    font-weight: 700;
    font-size: 24px;
    text-align: center;
    color: $black;
  }

  &__row {
    display: flex;
    width: 100%;
    flex-wrap: wrap;

    .field--half:last-child {
      margin-right: 0;
    }
  }

  .field {
    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }

    &--full {
      width: 100%;
      margin-right: 0 !important;
    }

    &--half {
      width: calc(50% - 5px);
      margin-right: 0;

      &:last-child {
        margin-left: auto;
      }

      @include max-md {
        width: 100%;
      }
    }
  }

  &__header {
    margin-bottom: 30px;
  }

  &__scrollable {
    height: calc(100% - 50px);
    overflow: auto;
    padding: 0 10px;
  }

  &__footer {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 20px 40px;
    background-color: #fff;
  }

  .field {
    margin-right: 10px;

    @include max-xs {
      margin-right: 0;
    }
  }
}
</style>
